import { Lender } from '@1delta/lender-registry'
import { LlamaLendAmmAbi } from '@1delta/abis'
import { formatUnits } from 'viem'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'

/**
 * Curve LlamaLend oracle fetcher — DERIVED (Pass 2).
 *
 * Each market's price feed is `price_oracle()` on its own LLAMMA. Three
 * properties of that read drive every decision in this file:
 *
 *  1. **It is always WAD**, regardless of either token's decimals. Verified
 *     on-chain across the decimal spread: the 8-decimal WBTC / crvUSD market
 *     returns `65042815002675129318680` (= 65,042.82) and the 18-decimal
 *     sfrxUSD market returns `1205489834170667241` (= 1.2055). So this fetcher
 *     divides by 1e18 and NEVER consults token decimals — unlike Morpho, whose
 *     oracles scale by `10^(36 + loanDec - collDec)`.
 *  2. **It is denominated in the BORROWED token**, not USD. Hence Pass 2 with
 *     `updatePrices=false`: `collateralUSD = ratio × borrowedUSD`, where the
 *     borrowed token's direct USD price came from Pass 1. This is the
 *     Morpho/Midnight/Teller shape, so the derivation class is `'derived'`.
 *     It matters for real markets, not just in theory — 8 of ~99 markets
 *     borrow something other than crvUSD (CRV, WETH, tBTC, ynETH, USDC,
 *     wstETH), where treating the ratio as USD would be badly wrong.
 *  3. **It lives on the AMM, and only on the AMM.** A Curve
 *     `price_oracle_contract` is a different contract exposing `price()`, and
 *     the LLAMMA does NOT implement `price()`. That distinction is the whole
 *     reason this fetcher exists: LlamaLend markets used to fall through a
 *     catch-all `else` into the MORPHO override bucket, which called `price()`
 *     on the LLAMMA — reverting, mapping to '0x', and dropping every market
 *     silently. Had the address been a `price_oracle_contract` instead, the
 *     call would have SUCCEEDED and been rescaled by 1e36, i.e. ~1e18 off.
 *
 * Markets are sourced exclusively from overrides (the database), like Morpho
 * and Lista. There is no on-chain market enumeration to fall back on.
 */

/**
 * One LlamaLend market, as supplied by the caller's database.
 */
export interface LlamaLendMarketOverride {
  /**
   * The market's LLAMMA. `price_oracle()` is read from here — NOT from the
   * generic `oracle` column, which is written as the AMM but would silently
   * become unreadable if a `price_oracle_contract` were ever stored there.
   */
  amm: string
  /** Borrowed token — the oracle's unit of account. */
  loanAsset: string
  collateralAsset: string
  /** Present for symmetry with the other override types; NOT used for scaling. */
  loanAssetDecimals?: number
  /** Present for symmetry with the other override types; NOT used for scaling. */
  collateralAssetDecimals?: number
  /**
   * Controller address, 0x-stripped and uppercased — the suffix of the
   * per-market lender key the lending converter emits.
   */
  marketId: string
}

export type LlamaLendMarketOverrides = {
  [chainId: string]: LlamaLendMarketOverride[]
}

export interface LlamaLendFetcherContext {
  marketOverrides?: LlamaLendMarketOverrides
}

interface LlamaLendGroupedMeta {
  markets: LlamaLendMarketOverride[]
}

/**
 * Per-market lender key: `LLAMALEND_<CONTROLLER_HEX_UPPER>`.
 *
 * MUST stay identical to `llamaLendLenderKey` in the lending converter — the
 * `marketUid` join is what carries these prices onto pool rows, and a mismatch
 * drops them with "market not in DB" rather than failing loudly.
 */
function generateLlamaLendLenderKey(marketId: string): string {
  return `${Lender.LLAMALEND}_${marketId.replace(/^0x/i, '').toUpperCase()}`
}

function getLlamaLendMarketsForChain(
  chainId: string,
  marketOverrides?: LlamaLendMarketOverrides,
): LlamaLendMarketOverride[] {
  return marketOverrides?.[chainId] ?? []
}

/**
 * Build the multicalls — one `price_oracle()` per market.
 *
 * Unlike Morpho, calls are NOT deduplicated by feed address: an LLAMMA is
 * per-market by construction, so two markets never share one. Grouping by
 * address would add a lookup layer with nothing to collapse.
 */
export function getLlamaLendCalls(
  chainId: string,
  context?: LlamaLendFetcherContext,
): FetcherResult<LlamaLendGroupedMeta>[] {
  const markets = getLlamaLendMarketsForChain(chainId, context?.marketOverrides)

  if (markets.length === 0) return []

  return markets.map((market) => {
    const call: MulticallCall = {
      address: market.amm,
      name: 'price_oracle',
      params: [],
    }

    return {
      calls: [call],
      meta: { markets: [market] },
      lender: generateLlamaLendLenderKey(market.marketId),
    }
  })
}

/**
 * Parse `price_oracle()` into a borrowed-token base entry plus a derived
 * collateral entry.
 *
 * Emits nothing when the borrowed token has no USD price yet — a ratio with no
 * base is not a price, and inventing one would put a number ~1/borrowedUSD off
 * into the risk math.
 */
export function parseLlamaLendResults(
  data: any[],
  meta: LlamaLendGroupedMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, usdPrices, tokenList } = context
  const entries: OraclePriceEntry[] = []

  const rawPrice = data[0]
  if (rawPrice === undefined || rawPrice === null || rawPrice === '0x') {
    return entries
  }

  for (const market of meta.markets) {
    const loanAsset = market.loanAsset.toLowerCase()
    const collateralAsset = market.collateralAsset.toLowerCase()

    let collateralInLoan: number
    try {
      // WAD, unconditionally — see (1) in the module comment. Reading token
      // decimals here would be the Morpho bug in reverse.
      collateralInLoan = Number(formatUnits(BigInt(rawPrice.toString()), 18))
    } catch {
      continue
    }

    if (!Number.isFinite(collateralInLoan) || collateralInLoan === 0) continue

    const loanOracleKey =
      tokenList?.[loanAsset]?.assetGroup ?? `${chainId}-${loanAsset}`
    const loanAssetUSD = usdPrices[loanOracleKey] ?? usdPrices[loanAsset]

    // No base price → no derived price. Silence beats a wrong number here.
    if (!loanAssetUSD) continue

    const lenderKey = generateLlamaLendLenderKey(market.marketId)

    // The borrowed token IS the oracle's unit of account, so it prices at
    // exactly 1.0 in the oracle's own denomination.
    entries.push({
      asset: loanAsset,
      price: 1,
      priceUSD: loanAssetUSD,
      marketUid: createMarketUid(chainId, lenderKey, loanAsset),
      targetLender: lenderKey,
      description: 'LlamaLend borrowed asset',
      staticBase: true,
      baseAsset: loanAsset,
    })

    entries.push({
      asset: collateralAsset,
      price: collateralInLoan,
      priceUSD: collateralInLoan * loanAssetUSD,
      marketUid: createMarketUid(chainId, lenderKey, collateralAsset),
      targetLender: lenderKey,
      description: 'LlamaLend collateral (AMM EMA oracle)',
      baseAsset: loanAsset,
    })
  }

  return entries
}

export function getLlamaLendAbi() {
  return LlamaLendAmmAbi
}

/**
 * Curve LlamaLend Oracle Fetcher module
 */
export const llamaLendFetcher = {
  getCalls: getLlamaLendCalls,
  parse: parseLlamaLendResults,
  getAbi: getLlamaLendAbi,
}
